import { create } from 'zustand';
import { toast } from 'sonner';
import * as contactsApi from './contactsApi';

export interface Contact {
  id: string;
  type: string;
  name: string;
  org: string;
  email: string;
  phone: string;
  /** Alternate / secondary number (Contact.mobilePhone). */
  mobilePhone?: string;
  cases: number;
  kyc: string;
  tags: string[];
  notes: string;
  createdAt: number;
  totalBilled?: number;
  paymentRating?: 'excellent' | 'good' | 'slow' | 'overdue';
  instructsIn?: string[];
  preferredBarristers?: string[];
}

interface ContactsState {
  contacts: Contact[];
  loading: boolean;
  error: string | null;
  fetchContacts: () => Promise<void>;
  addContact: (input: Omit<Contact, 'id' | 'createdAt'>) => Promise<Contact>;
  updateContact: (id: string, patch: Partial<Contact>) => void;
  deleteContact: (id: string) => void;
}

export const useContactsStore = create<ContactsState>()((set, get) => ({
  contacts: [],
  loading: false,
  error: null,

  fetchContacts: async () => {
    set({ loading: true, error: null });
    try {
      const contacts = await contactsApi.fetchContacts();
      set({ contacts, loading: false });
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
      set({ loading: false, error: 'Failed to load contacts' });
    }
  },

  addContact: async (input) => {
    const tempContact: Contact = { ...input, id: `temp-${Date.now()}`, createdAt: Date.now() };
    set((s) => ({ contacts: [tempContact, ...s.contacts] }));

    // Map frontend type back to API type
    const typeMap: Record<string, string> = {
      lay: 'lay_client', defendant: 'defendant', solicitor: 'solicitor', company: 'firm',
      expert: 'expert', witness: 'witness', other: 'other',
    };

    try {
      const realContact = await contactsApi.createContact({
        type: typeMap[input.type] ?? input.type,
        firstName: input.name.split(' ').slice(0, -1).join(' ') || input.name,
        lastName: input.name.split(' ').slice(-1)[0] || undefined,
        company: input.org !== '—' ? input.org : undefined,
        email: input.email || undefined,
        phone: input.phone || undefined,
        mobilePhone: input.mobilePhone || undefined,
        notes: input.notes || undefined,
        tags: input.tags,
        firmName: input.type === 'solicitor' ? input.org : undefined,
      });
      // Replace the optimistic temp with the persisted record.
      set((s) => ({
        contacts: s.contacts.map((c) => c.id === tempContact.id ? realContact : c),
      }));
      return realContact;
    } catch (err) {
      console.error('Failed to create contact:', err);
      set((s) => ({ contacts: s.contacts.filter((c) => c.id !== tempContact.id) }));
      throw err;
    }
  },

  updateContact: (id, patch) => {
    const current = get().contacts.find((c) => c.id === id);
    set((s) => ({
      contacts: s.contacts.map((c) => c.id === id ? { ...c, ...patch } : c),
    }));

    // Map the web patch back to the API shape. Previously ONLY notes/tags were persisted, so
    // edits to a contact's name / email / phone / alternate number were silently lost on refresh
    // and never reflected elsewhere. Persist every editable field.
    const typeMap: Record<string, string> = {
      lay: 'lay_client', defendant: 'defendant', solicitor: 'solicitor', company: 'firm',
      expert: 'expert', witness: 'witness', other: 'other',
    };
    const effType = patch.type ?? current?.type ?? '';
    const apiType = typeMap[effType] ?? effType;

    const apiPatch: Record<string, unknown> = {};
    if (patch.type !== undefined) apiPatch.type = apiType;
    if (patch.email !== undefined) apiPatch.email = patch.email || undefined;
    if (patch.phone !== undefined) apiPatch.phone = patch.phone || undefined;
    if (patch.mobilePhone !== undefined) apiPatch.mobilePhone = patch.mobilePhone || undefined;
    if (patch.notes !== undefined) apiPatch.notes = patch.notes;
    if (patch.tags !== undefined) apiPatch.tags = patch.tags;
    if (patch.name !== undefined) {
      // 'firm' contacts carry their name in company; everyone else in firstName/lastName
      // (contactName reads firstName+lastName for all non-firm types, incl. solicitor).
      if (apiType === 'firm') {
        apiPatch.company = patch.name;
      } else {
        apiPatch.firstName = patch.name.split(' ').slice(0, -1).join(' ') || patch.name;
        apiPatch.lastName = patch.name.split(' ').slice(-1)[0] || undefined;
      }
    }
    if (patch.org !== undefined) {
      apiPatch.company = patch.org && patch.org !== '—' ? patch.org : undefined;
      if (apiType === 'solicitor') apiPatch.firmName = patch.org && patch.org !== '—' ? patch.org : undefined;
    }

    if (Object.keys(apiPatch).length > 0) {
      contactsApi.updateContact(id, apiPatch).catch((err) => {
        console.error('Failed to update contact:', err);
        // The optimistic patch above has already redrawn the row, and every caller announces
        // "Contact updated" / "Notes saved" without waiting for this promise. Refetching alone
        // therefore made a REFUSED edit look like a successful one that quietly reverted — the
        // caller saw a success toast, the field changed, then changed back with no explanation.
        // Say so, exactly as `deleteContact` below already does. The refetch still follows, so the
        // server's answer is what ends up on screen.
        get().fetchContacts();
        toast.error('Could not save this client — you may not have permission');
      });
    }
  },

  deleteContact: (id) => {
    const removed = get().contacts.find((c) => c.id === id);
    set((s) => ({ contacts: s.contacts.filter((c) => c.id !== id) }));

    contactsApi.deleteContact(id).catch((err) => {
      console.error('Failed to delete contact:', err);
      if (removed) set((s) => ({ contacts: [removed, ...s.contacts] }));
      toast.error('Could not remove client — you may not have permission');
    });
  },
}));
